import { useEffect, useRef, useState } from 'react'
import { supabase } from '@/lib/supabase'
import { Loader2, ShieldCheck } from 'lucide-react'

interface Props {
  /** Called once the TOTP code is verified and the session reaches aal2. */
  onVerified: () => void
  onSignOut: () => void
}

/** Login-time second factor — prompts for the 6-digit authenticator code. */
export function MfaChallenge({ onVerified, onSignOut }: Props) {
  const [code, setCode] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [factorId, setFactorId] = useState<string | null>(null)
  const inputRef = useRef<HTMLInputElement | null>(null)

  useEffect(() => {
    supabase.auth.mfa.listFactors().then(({ data, error }) => {
      if (error) {
        setError(error.message)
        return
      }
      const totp =
        data?.totp?.find((f) => f.status === 'verified') ?? data?.totp?.[0]
      if (!totp) {
        setError("Aucun facteur d'authentification trouvé.")
        return
      }
      setFactorId(totp.id)
    })
    inputRef.current?.focus()
  }, [])

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!factorId || code.trim().length < 6) return
    setBusy(true)
    setError(null)
    try {
      const { data: ch, error: chErr } = await supabase.auth.mfa.challenge({
        factorId,
      })
      if (chErr) throw chErr
      const { error: vErr } = await supabase.auth.mfa.verify({
        factorId,
        challengeId: ch.id,
        code: code.trim(),
      })
      if (vErr) throw vErr
      onVerified()
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err))
      setCode('')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-screen w-screen items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-sm">
        <div className="mb-6 flex flex-col items-center gap-2.5 text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--color-accent-bg)] text-[var(--color-accent-text)] shadow-[var(--shadow-soft)]">
            <ShieldCheck className="h-7 w-7" />
          </div>
          <div>
            <div className="text-[18px] font-semibold text-[var(--color-text-strong)]">
              Vérification en deux étapes
            </div>
            <div className="text-[12px] text-[var(--color-text-dim)]">
              Saisis le code de ton application d'authentification.
            </div>
          </div>
        </div>
        <form
          onSubmit={submit}
          className="rounded-3xl border border-[var(--color-border-soft)] bg-[var(--color-surface)] p-6 shadow-[var(--shadow-modal)]"
        >
          <input
            ref={inputRef}
            value={code}
            onChange={(e) =>
              setCode(e.target.value.replace(/\D/g, '').slice(0, 6))
            }
            inputMode="numeric"
            autoComplete="one-time-code"
            placeholder="123456"
            className="w-full rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)] px-3 py-2.5 text-center text-[20px] font-semibold tracking-[0.3em] text-[var(--color-text-strong)] outline-none transition-colors focus:border-[var(--color-link)]"
          />
          {error && (
            <div className="mt-3 rounded-xl border border-red-200 bg-[var(--color-danger-bg)] px-3 py-2 text-[11.5px] text-[var(--color-danger)]">
              {error}
            </div>
          )}
          <button
            type="submit"
            disabled={busy || code.length < 6 || !factorId}
            className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-[var(--color-accent)] px-4 py-2.5 text-[13px] font-semibold text-white shadow-[var(--shadow-soft)] transition-all hover:bg-[var(--color-accent-hover)] disabled:cursor-not-allowed disabled:opacity-60"
          >
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Vérifier
          </button>
          <button
            type="button"
            onClick={onSignOut}
            className="mt-3 block w-full text-center text-[12px] text-[var(--color-text-dim)] transition-colors hover:text-[var(--color-text)]"
          >
            Se déconnecter
          </button>
        </form>
      </div>
    </div>
  )
}
